"use client";

import Link from "next/link";
import { useImpressionOnce } from "@/hooks/useImpressionOnce";
import { trackEvent } from "@/lib/analytics-events";
import { governedActionEventProperties } from "@/lib/governed-action";
import type { AccountRelationshipAction, AccountRelationshipHome } from "./account-api";

export function AccountRelationshipTelemetry({ home }: { home: AccountRelationshipHome }) {
  useImpressionOnce(
    `my-jyotishi-home|${home.current_reading_id}|${home.state_version}`,
    () => {
      trackEvent("my_jyotishi_home_viewed", homeEventProperties(home));
    },
    { persist: true }
  );
  useImpressionOnce(
    home.today_guidance ? `my-jyotishi-daily-value|${home.today_guidance.artifact_id}` : null,
    () => {
      trackEvent("my_jyotishi_daily_value_seen", {
        ...homeEventProperties(home),
        as_of: home.today_guidance?.as_of,
        guidance_artifact_id: home.today_guidance?.artifact_id,
        guidance_version: home.today_guidance?.guidance_version,
        guidance_status: home.today_guidance?.status,
        guidance_rule_id: home.today_guidance?.personal_focus?.rule_id,
        degraded_sections: home.today_guidance?.degraded_sections.join(",") || undefined,
      });
    },
    { persist: true }
  );
  return null;
}

export function AccountRelationshipActionLink({
  action,
  language,
  relationshipMeasurementId,
}: {
  action: AccountRelationshipAction;
  language: string;
  relationshipMeasurementId: string;
}) {
  const props = {
    ...actionEventProperties(action),
    relationship_measurement_id: relationshipMeasurementId,
  };
  useImpressionOnce(action.presentation_key, () => trackEvent("journey_action_impression", props), {
    persist: true,
  });
  if (!action.destination) return null;
  return (
    <Link
      href={action.destination}
      onClick={() => trackEvent("journey_action_clicked", props)}
      className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[var(--color-accent)] px-5 py-3 text-sm font-semibold text-white"
    >
      {actionLabel(action.kind, language)}
    </Link>
  );
}

function homeEventProperties(home: AccountRelationshipHome): Record<string, unknown> {
  return {
    surface: "account_home",
    relationship_measurement_id: home.relationship_measurement_id,
    relationship_stage: home.relationship_stage,
    state_version: home.state_version,
    has_active_concern: Boolean(home.active_concern),
    has_remembered_opener: Boolean(home.remembered_opener),
    has_daily_guidance: Boolean(home.today_guidance),
    journey_action_id: home.next_action?.journey_action_id,
  };
}

function actionEventProperties(action: AccountRelationshipAction): Record<string, unknown> {
  return {
    ...governedActionEventProperties(action),
    surface: "account_home",
    action_id: action.action_id,
    presentation_key: action.presentation_key,
    slot: action.slot,
    relationship_stage: action.lifecycle_stage,
    placement: action.placement,
    reason_code: action.reason_code,
  };
}

function actionLabel(kind: string, language: string): string {
  const isHindi = language === "hi";
  const labels: Record<string, [string, string]> = {
    complete_chart: ["Complete your kundali", "Kundali details poori karein"],
    deepen_concern: ["Continue this concern", "Is concern par aage baat karein"],
    daily_checkin: ["Ask about today", "Aaj ke baare mein poochein"],
    re_engage: ["Continue your reading", "Reading continue karein"],
    continue_reading: ["Continue your reading", "Reading continue karein"],
  };
  const label = labels[kind] ?? labels.continue_reading;
  return label[isHindi ? 1 : 0];
}
